import { AppBundle } from 'api/optimalprint-sdk.d';
import cn from 'classnames';
import Badge from 'components/Common/Badge/Badge';
import OpFavourite from 'components/Common/op-ui-wrappers/OpFavourite/OpFavourite';
import Price from 'components/Common/Price/Price';
import PricingBadge, { PricingBadgeSize } from 'components/Common/Prices/PricingBadge/PricingBadge';
import ProductPageLink from 'components/Common/ProductPageLink/ProductPageLink';
import { ProductType, WALLPAPER_PRODUCT_TYPES } from 'constants/app';
import useOnScreen from 'hooks/useOnScreen';
import React, { useRef, useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Store } from 'store';
import sendGtmProductClickOperation from 'store/category/operation/sendGtmProductClickOperation';
import sendGtmProductImpressionOperation from 'store/category/operation/sendGtmProductImpressionOperation';
import getPrice from 'store/price/selector/getPrice';
import calculateDiscountPercent from 'utils/price/calculateDiscountPercent';
import styles from './ProductItem.module.scss';

export interface OwnProps {
  isReactCategory: boolean;
  product: AppBundle.Api.Response.Category.V3.CategoryDesignsListV3Response.Family.IDesign;
  index: number;
  trackImpression?: boolean;
}

const ProductItem = ({ isReactCategory, product, index, trackImpression }: OwnProps) => {
  const { t } = useTranslation();
  const ref = useRef(null);
  const [refUpdateCounter, incRefUpdateCounter] = useState(0);
  const isVisible = useOnScreen(ref, refUpdateCounter);
  const [isImpressionTracked, setIsImpressionTracked] = useState(!trackImpression);

  const dispatch = useDispatch();

  const productClick = () => {
    dispatch(sendGtmProductClickOperation(product.designIdEncrypted, index, 'Category Page'));
  };

  const price = useSelector((store: Store) => getPrice(store, product.categoryId || 0, product.productId, 1));
  const hasDiscount = price && price.pi !== price.p;

  const refWrapper = useCallback((node) => {
    incRefUpdateCounter((v) => v + 1);
    ref.current = node;
  }, []);

  useEffect(() => {
    if (isVisible && !isImpressionTracked) {
      dispatch(sendGtmProductImpressionOperation([product.designIdEncrypted], [index]));
      setIsImpressionTracked(true);
    }
  }, [isVisible, isImpressionTracked]);

  if (!price) {
    return null;
  }

  const discountedPercent = calculateDiscountPercent(price.p, price.pi);

  // @todo remove this hack
  const isPoster = product.productTypeId === ProductType.StudioSelectionPoster;
  const isWallpaper = WALLPAPER_PRODUCT_TYPES.includes(product.productTypeId);

  const previews = [product.thumbnail, product.secondaryPreview];
  const [thumbnail, secondaryPreview] = isWallpaper ? previews.reverse() : previews;

  const pricePostfix = isWallpaper ? t('txt_per_square_meter') : '';
  return (
    <ProductPageLink
      link={product.productPageUrl}
      isReact={isReactCategory}
      className={cn(styles.productContainer, { [styles.productWallpaper]: isWallpaper })}
      onClick={productClick}
    >
      <div ref={refWrapper} className={styles.product}>
        <div className={cn(styles.favIcon, styles.showAlways)}>
          {product.productSKU && (
            <OpFavourite sku={product.productSKU} productTier={product.productTier} categoryId={product.categoryId} />
          )}
        </div>
        <div className={cn(styles.previewContainer, { [styles.previewContainerPoster]: isPoster })}>
          <div className={styles.productImage}>
            <img src={thumbnail} loading="lazy" className={styles.preview} alt="" />
            {secondaryPreview && (
              <img src={secondaryPreview} loading="lazy" className={styles.secondaryPreview} alt="" />
            )}
          </div>
        </div>
        <div className={styles.captions}>
          <div className={styles.artistName}>{product.designAuthorName || t('txt_design_author_name_placeholder')}</div>
          <div className={styles.productName}>{product.designName || t('txt_design_name_placeholder')}</div>
          <div className={cn(styles.price, styles.flexCol)}>
            {hasDiscount && (
              <div className={styles.pricingBadge}>
                <PricingBadge discount={discountedPercent} fontSize={PricingBadgeSize.sm} />
              </div>
            )}
            <Price
              prefix={t('txt_from')}
              postfix={pricePostfix}
              categoryId={product.categoryId}
              productId={product.productId}
            />
          </div>
        </div>
        <div className={styles.badge}>
          {product.isNew && <Badge label={t('txt_new')} />}
          {product.isBestseller && <Badge label={t('txt_bestseller')} />}
        </div>
      </div>
    </ProductPageLink>
  );
};

export default ProductItem;
